import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useLogout, useMe } from '../../api/hooks/useAuth.ts';
import { useCreatePlan, useDeletePlan, usePlans } from '../../api/hooks/usePlans.ts';
import { buttonClass, inputClass } from '../../components/AuthCard.tsx';

export default function PlanList() {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const plans = usePlans();
  const createPlan = useCreatePlan();
  const deletePlan = useDeletePlan();
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState('');
  const [businessName, setBusinessName] = useState('');

  function onCreate(e: FormEvent) {
    e.preventDefault();
    createPlan.mutate(
      { title, business_name: businessName },
      { onSuccess: (doc) => void navigate(`/plans/${doc.plan.id}/edit`) },
    );
  }

  return (
    <main className="mx-auto max-w-4xl p-8">
      <header className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">planb</h1>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-600">{me.data?.user.display_name}</span>
          <button
            className="text-blue-600 hover:underline"
            onClick={() =>
              logout.mutate(undefined, {
                onSuccess: () => void navigate('/login', { replace: true }),
              })
            }
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-700">Your plans</h2>
        <button className="text-sm font-semibold text-blue-600 hover:underline" onClick={() => setShowNew((v) => !v)}>
          {showNew ? 'Cancel' : '+ New plan'}
        </button>
      </div>

      {showNew && (
        <form onSubmit={onCreate} className="mb-8 flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <label className="flex-1">
            <span className="mb-1 block text-xs font-medium text-gray-600">Plan title</span>
            <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} required />
          </label>
          <label className="flex-1">
            <span className="mb-1 block text-xs font-medium text-gray-600">Business name</span>
            <input className={inputClass} value={businessName} onChange={(e) => setBusinessName(e.target.value)} required />
          </label>
          <button className={`${buttonClass} w-auto`} type="submit" disabled={createPlan.isPending}>
            {createPlan.isPending ? 'Creating…' : 'Create'}
          </button>
        </form>
      )}

      {plans.isPending && <p className="text-sm text-gray-400">Loading…</p>}
      {plans.data && plans.data.plans.length === 0 && !showNew && (
        <section className="rounded-xl border border-dashed border-gray-300 p-12 text-center text-gray-500">
          No plans yet — create your first one.
        </section>
      )}
      <ul className="space-y-3">
        {plans.data?.plans.map((p) => (
          <li key={p.id} className="flex items-center justify-between rounded-xl border border-gray-200 p-4">
            <div>
              <Link
                to={p.role === 'owner' ? `/plans/${p.id}/edit` : `/plans/${p.id}/view`}
                className="font-semibold text-gray-800 hover:text-blue-700"
              >
                {p.title}
              </Link>
              <p className="text-sm text-gray-500">
                {p.business_name} ·{' '}
                <span className={p.status === 'published' ? 'text-green-700' : 'text-amber-600'}>{p.status}</span>
                {p.role === 'viewer' && (
                  <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                    shared with you
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <Link className="text-blue-600 hover:underline" to={`/plans/${p.id}/view`}>
                View
              </Link>
              {p.role === 'owner' && (
                <>
                  <Link className="text-blue-600 hover:underline" to={`/plans/${p.id}/edit`}>
                    Edit
                  </Link>
                  <Link className="text-blue-600 hover:underline" to={`/plans/${p.id}/share`}>
                    Share
                  </Link>
                  <button
                    className="text-red-500 hover:underline"
                    onClick={() => {
                      if (window.confirm(`Delete "${p.title}"? This cannot be undone.`)) {
                        deletePlan.mutate(p.id);
                      }
                    }}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </main>
  );
}
